// Deterministic replays of proposals and approvals for Lesson 4.9 ("Some Actions Need a
// Person"). Every lookup, proposal, tool result and closing sentence below was recorded from
// real runs on 8 October 2026 with zai.glm-5 over the read tools of Unit 2 and six write tools
// whose descriptions say the person approves first. The graphics replay those recordings and
// never call a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export const BADGES_PROPOSAL =
  'Assign task 12 "Order ten more name badges" (Apollo, open, unassigned) to Ravi.';
export const DELETE_PROPOSAL =
  'Delete task 15 "Write the release note for Apollo" (Apollo, open, unassigned).';
export const ADD_MEMBER_PROPOSAL = 'Add Sam (user 4) to Apollo as member.';

// --- approve-rating: what may run at once, and what must wait ------------------------------

export const TOOLS = [
  'list_tasks',
  'assign_task',
  'create_task',
  'set_completed',
  'delete_task',
  'add_member',
] as const;
export type Tool = (typeof TOOLS)[number];
export const toTool = (value: string): Tool => TOOLS.find((tool) => tool === value) ?? 'list_tasks';
export type Rating = 'read' | 'write' | 'dangerous';
export const ratings: Record<Tool, Rating> = {
  list_tasks: 'read',
  assign_task: 'write',
  create_task: 'write',
  set_completed: 'write',
  delete_task: 'dangerous',
  add_member: 'dangerous',
};
export const loopActions: Record<Rating, string> = {
  read: 'runs it at once',
  write: 'stores a proposal and pauses',
  dangerous: 'pauses and shows everything it would change',
};
export const NOTHING_TO_APPROVE = '(nothing, a read needs no approval)';
/** The sentence code writes from the database for the approval. */
export const approvalShows: Record<Tool, string> = {
  list_tasks: NOTHING_TO_APPROVE,
  assign_task: BADGES_PROPOSAL,
  create_task: 'Create task "Check the microphone" in Apollo.',
  set_completed:
    'Mark task 14 "Send the final headcount to the venue" (Apollo, open, unassigned) as done.',
  delete_task: DELETE_PROPOSAL,
  add_member: ADD_MEMBER_PROPOSAL,
};
export const runsBeforeApproval = (tool: Tool) => (ratings[tool] === 'read' ? 'yes' : 'no');
export interface RatingState {
  tool: Tool;
  rated: boolean;
}
export const initialRating = (): RatingState => ({ tool: 'list_tasks', rated: false });
export type RatingAction = { type: 'tool'; value: Tool } | { type: 'rate' } | { type: 'reset' };
export function ratingTransition(state: RatingState, action: RatingAction): RatingState {
  switch (action.type) {
    case 'reset':
      return initialRating();
    case 'tool':
      return { tool: action.value, rated: false };
    case 'rate':
      return { ...state, rated: true };
  }
}
export const RATING_START_MESSAGE = 'Choose a tool, then press Rate.';
export const ratingMessages: Record<Rating, string> = {
  read: 'A read changes nothing, so the loop runs it at once.',
  write: 'A write changes a row, so the loop stores a proposal and pauses.',
  dangerous: 'A dangerous write cannot be undone, so the approval shows what it would destroy.',
};
export const RATING_COUNTEREXAMPLE =
  'A write that ran at once because it looked small is a change nobody approved.';
export const RATING_REFINEMENT =
  'Refinement: every change pauses, a dangerous one shows what it would destroy.';
export function ratingSummary(state: RatingState): Summary {
  if (!state.rated) return { tone: 'neutral', message: RATING_START_MESSAGE };
  const rating = ratings[state.tool];
  return { tone: rating === 'read' ? 'neutral' : 'success', message: ratingMessages[rating] };
}

// --- approve-pause: what the assistant does with a command ---------------------------------

export const COMMANDS = ['badges', 'delete', 'create', 'bulk'] as const;
export type Command = (typeof COMMANDS)[number];
export const commandLabels: Record<Command, string> = {
  badges: 'Assign the badges task to Ravi.',
  delete: 'Delete the duplicate release note task.',
  create: 'Create a task to check the microphone.',
  bulk: 'Assign every open task to Jo.',
};
export const toCommand = (value: string): Command =>
  COMMANDS.find((command) => command === value) ?? 'badges';
export interface Proposal {
  call: string;
  /** The sentence code writes from the database. */
  sentence: string;
}
export interface OpenTask {
  id: number;
  title: string;
  assignee: string;
}
/** The open Apollo tasks the bulk command proposed, in the recorded order. */
export const OPEN_TASKS: readonly OpenTask[] = [
  { id: 9, title: 'Write the release note', assignee: 'assigned to Ravi' },
  { id: 10, title: 'Check the dates', assignee: 'unassigned' },
  { id: 12, title: 'Order ten more name badges', assignee: 'unassigned' },
  { id: 13, title: 'Check the projector in the small room', assignee: 'unassigned' },
  { id: 14, title: 'Send the final headcount to the venue', assignee: 'unassigned' },
  { id: 15, title: 'Write the release note for Apollo', assignee: 'unassigned' },
];
export const bulkProposal = (task: OpenTask): Proposal => ({
  call: `assign_task(task_id ${task.id}, user_id 3)`,
  sentence: `Assign task ${task.id} "${task.title}" (Apollo, open, ${task.assignee}) to Jo.`,
});
export interface CommandRun {
  lookups: readonly string[];
  proposals: readonly Proposal[];
  /** The recorded reply: the closing sentence, or the question the model asked back. */
  reply: string;
  tone: Tone;
}
const LIST_PROJECTS = 'list_projects()';
const LIST_TASKS = ['list_tasks(project_id 1)', 'list_tasks(project_id 2)'];
export const BADGES_CLOSING =
  'I\'ve proposed assigning the task "Order ten more name badges" to Ravi. This is awaiting your approval before it takes effect.';
export const DELETE_QUESTION =
  'I found two release note tasks in the Apollo project: "Write the release note" (assigned to Ravi) and "Write the release note for Apollo" (unassigned). Which one should I delete?';
export const CREATE_QUESTION =
  'You\'re a manager in two projects: Apollo and Support. Which project should I create the "check the microphone" task in?';
export const BULK_CLOSING =
  "I've proposed assigning 6 open tasks to Jo across both Apollo and Support projects. These assignments are now awaiting your approval before they take effect.";
export const commandRuns: Record<Command, CommandRun> = {
  badges: {
    lookups: [LIST_PROJECTS, ...LIST_TASKS, 'list_members(project_id 1)'],
    proposals: [{ call: 'assign_task(task_id 12, user_id 2)', sentence: BADGES_PROPOSAL }],
    reply: BADGES_CLOSING,
    tone: 'success',
  },
  delete: {
    lookups: [LIST_PROJECTS, ...LIST_TASKS],
    proposals: [],
    reply: DELETE_QUESTION,
    tone: 'neutral',
  },
  create: { lookups: [LIST_PROJECTS], proposals: [], reply: CREATE_QUESTION, tone: 'neutral' },
  bulk: {
    lookups: [
      LIST_PROJECTS,
      ...LIST_TASKS,
      'list_members(project_id 1)',
      'list_members(project_id 2)',
    ],
    proposals: OPEN_TASKS.map(bulkProposal),
    reply: BULK_CLOSING,
    tone: 'warning',
  },
};
export const NO_PROPOSALS = '(none, the model asked back)';
export const proposalsText = (run: CommandRun) =>
  run.proposals.length ? run.proposals.map((entry) => entry.sentence).join('\n') : NO_PROPOSALS;
export const lookupsText = (run: CommandRun) => run.lookups.join('\n');
export const replyLabel = (run: CommandRun) =>
  run.proposals.length ? "The model's closing sentence" : 'The question the model asked back';
export const WRITES_MADE = '0';
export interface PauseState {
  command: Command;
  sent: boolean;
}
export const initialPause = (): PauseState => ({ command: 'badges', sent: false });
export type PauseAction =
  { type: 'command'; value: Command } | { type: 'send' } | { type: 'reset' };
export function pauseTransition(state: PauseState, action: PauseAction): PauseState {
  switch (action.type) {
    case 'reset':
      return initialPause();
    case 'command':
      return { command: action.value, sent: false };
    case 'send':
      return { ...state, sent: true };
  }
}
export const PAUSE_START_MESSAGE = 'Choose a command, then press Send.';
export const pauseMessages: Record<Command, string> = {
  badges: 'One proposal waits, and nothing changed until Maya decides.',
  delete: 'Two tasks matched, so the model asked which one before proposing.',
  create: 'The project was unclear, so the model asked instead of guessing.',
  bulk: 'Six proposals wait together, and Maya can refuse every one of them.',
};
export const PAUSE_REFINEMENT = 'Refinement: the model proposes, code pauses, the person decides.';
export function pauseSummary(state: PauseState): Summary {
  if (!state.sent) return { tone: 'neutral', message: PAUSE_START_MESSAGE };
  return { tone: commandRuns[state.command].tone, message: pauseMessages[state.command] };
}

// --- approve-resume: what happens after Maya decides ---------------------------------------

export const DECISIONS = ['approve', 'reject'] as const;
export type Decision = (typeof DECISIONS)[number];
export const toDecision = (value: string): Decision =>
  DECISIONS.find((decision) => decision === value) ?? 'approve';
export const APPROVED_RESULT =
  '{"id": 12, "project_id": 1, "title": "Order ten more name badges", "assignee_id": 2, "completed": false}';
export const REJECTED_RESULT =
  '{"status": "rejected", "message": "Maya rejected this change; nothing changed."}';
export const APPROVED_CLOSING =
  'The task "Order ten more name badges" has been successfully assigned to Ravi.';
export const REJECTED_CLOSING =
  'The assignment was rejected, so the "Order ten more name badges" task remains unassigned. Would you like to try a different assignment or make any other changes?';
export interface DecisionRun {
  result: string;
  closing: string;
  rowsChanged: number;
  audit: string;
}
export const decisionRuns: Record<Decision, DecisionRun> = {
  approve: {
    result: APPROVED_RESULT,
    closing: APPROVED_CLOSING,
    rowsChanged: 1,
    audit: 'decided: approved',
  },
  reject: {
    result: REJECTED_RESULT,
    closing: REJECTED_CLOSING,
    rowsChanged: 0,
    audit: 'decided: rejected',
  },
};
export interface ResumeState {
  decision: Decision;
  decided: boolean;
}
export const initialResume = (): ResumeState => ({ decision: 'approve', decided: false });
export type ResumeAction =
  { type: 'decision'; value: Decision } | { type: 'decide' } | { type: 'reset' };
export function resumeTransition(state: ResumeState, action: ResumeAction): ResumeState {
  switch (action.type) {
    case 'reset':
      return initialResume();
    case 'decision':
      return { decision: action.value, decided: false };
    case 'decide':
      return { ...state, decided: true };
  }
}
export const RESUME_START_MESSAGE = 'Choose a decision, then press Decide.';
export const RESUME_APPROVE_MESSAGE =
  'The approval ran the tool as Maya, and the model closed from the row it got back.';
export const RESUME_REJECT_MESSAGE =
  'The rejection was a tool result too, so the model knew nothing changed.';
export const RESUME_COUNTEREXAMPLE =
  'Without that result the model would close as if the change had happened.';
export const RESUME_REFINEMENT =
  'Refinement: the decision is a tool result, and the model closes from it.';
export function resumeSummary(state: ResumeState): Summary {
  if (!state.decided) return { tone: 'neutral', message: RESUME_START_MESSAGE };
  return state.decision === 'approve'
    ? { tone: 'success', message: RESUME_APPROVE_MESSAGE }
    : { tone: 'success', message: RESUME_REJECT_MESSAGE };
}

// --- approve-audit: who approved what, and did it run --------------------------------------

export const APPROVERS = ['maya', 'ravi'] as const;
export type Approver = (typeof APPROVERS)[number];
export const approverLabels: Record<Approver, string> = {
  maya: 'Maya, manager',
  ravi: 'Ravi, member',
};
export const toApprover = (value: string): Approver =>
  APPROVERS.find((approver) => approver === value) ?? 'maya';
export const AUDIT_COMMAND = 'Add Sam to Apollo';
export const PROPOSED = 'add_member(project 1, user 4, member)';
export const REFUSAL = 'Only a manager of this project can do that.';
export const RAVI_CLOSING = 'I see that only a manager of the Apollo project can add members.';
export interface AuditRow {
  askedBy: string;
  proposed: string;
  rating: Rating;
  decidedBy: string;
  decision: string;
  outcome: string;
}
export const auditRows: Record<Approver, AuditRow> = {
  maya: {
    askedBy: 'Maya',
    proposed: PROPOSED,
    rating: 'dangerous',
    decidedBy: 'Maya',
    decision: 'approved',
    outcome: 'done, Sam is now a member',
  },
  ravi: {
    askedBy: 'Ravi',
    proposed: PROPOSED,
    rating: 'dangerous',
    decidedBy: 'Ravi',
    decision: 'approved',
    outcome: `refused, "${REFUSAL}"`,
  },
};
export const auditTable = (approver: Approver): string[][] => {
  const entry = auditRows[approver];
  return [
    ['Asked by', entry.askedBy],
    ['Proposed', entry.proposed],
    ['Rating', entry.rating],
    ['Decided by', entry.decidedBy],
    ['Decision', entry.decision],
    ['Outcome', entry.outcome],
  ];
};
export const permitted = (approver: Approver) => (approver === 'maya' ? 'yes' : 'no');
export const auditRowsChanged = (approver: Approver) => (approver === 'maya' ? '1' : '0');
export const RAVI_NOTE = `Ravi then read: "${RAVI_CLOSING}"`;
export interface AuditState {
  approver: Approver;
  approved: boolean;
}
export const initialAudit = (): AuditState => ({ approver: 'maya', approved: false });
export type AuditAction =
  { type: 'approver'; value: Approver } | { type: 'approve' } | { type: 'reset' };
export function auditTransition(state: AuditState, action: AuditAction): AuditState {
  switch (action.type) {
    case 'reset':
      return initialAudit();
    case 'approver':
      return { approver: action.value, approved: false };
    case 'approve':
      return { ...state, approved: true };
  }
}
export const AUDIT_START_MESSAGE = 'Choose who asks and approves, then press Approve.';
export const AUDIT_MAYA_MESSAGE =
  'Maya may add members, so the approved change ran and was written down.';
export const AUDIT_RAVI_MESSAGE =
  'Ravi approved, but the service refused, and the row records both.';
export const AUDIT_REFINEMENT =
  'Refinement: approval is consent, permission is code, and both are written down.';
export function auditSummary(state: AuditState): Summary {
  if (!state.approved) return { tone: 'neutral', message: AUDIT_START_MESSAGE };
  return state.approver === 'maya'
    ? { tone: 'success', message: AUDIT_MAYA_MESSAGE }
    : { tone: 'warning', message: AUDIT_RAVI_MESSAGE };
}
